import { Stage } from 'konva/lib/Stage';
import { forwardRef, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faArrowRight, faDownload } from '@fortawesome/free-solid-svg-icons';
import Canvas, { LineProps } from '@/components/Controls/Canvas';
import * as queries from '@/graphql/queries';
import { useAmplifyApiClient } from '@/hooks/AmplifyApiClientHooks';
import { usePlayer, useSession } from '@/hooks/PlayerSessionControlHooks';
import { useParams } from 'react-router-dom';
import CanvasShare from '@/components/Controls/CanvasShare';
import AnimatedFeelingAfter from '@/components/Animated/Backgrounds/AnimatedFeelingAfterBackground';
import AnimatedDrawingCards from '@/components/Animated/Backgrounds/AnimatedDrawingCards';

interface CardsScreenProps {
  drawings: { prompt: string; drawing: LineProps[] }[];
  selectedIndex: number;
  onIndexChange: (index: number) => void;
  onDownload: () => void;
}

/**
 * Displays a thank you message with enhanced animation
 * Automatically transitions to cards screen
 */
const ThanksScreen = ({ onTransitionToCards }: { onTransitionToCards: () => void }) => {
  const [animationStage, setAnimationStage] = useState(0);

  useEffect(() => {
    let timer1: NodeJS.Timeout;
    let timer2: NodeJS.Timeout;

    if (animationStage === 0) {
      timer1 = setTimeout(() => {
        setAnimationStage(1);
      }, 2000);
    }

    if (animationStage === 1) {
      timer2 = setTimeout(() => {
        onTransitionToCards();
      }, 2500);
    }

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, [animationStage, onTransitionToCards]);

  return (
    <motion.div
      className="flex flex-col items-center justify-center min-h-[60vh]"
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{
        opacity: 1,
        scale: 1,
        transition: {
          duration: 0.6,
          type: 'spring',
          bounce: 0.4
        }
      }}
      exit={{
        opacity: 0,
        scale: 1.2,
        transition: {
          duration: 0.4
        }
      }}
    >
      <AnimatePresence mode="wait">
        {animationStage === 0 && (
          <motion.div
            key="thanks"
            initial={{ opacity: 0 }}
            animate={{
              opacity: 1,
              transition: {
                type: 'spring',
                stiffness: 100
              }
            }}
            exit={{
              opacity: 0,
              transition: {
                duration: 0.3
              }
            }}
          >
            <motion.h1
              className="text-5xl font-extrabold text-[#20145F] text-center"
              initial={{ opacity: 0, y: -50 }}
              animate={{
                opacity: 1,
                y: 0,
                transition: {
                  type: 'spring',
                  stiffness: 100
                }
              }}
            >
              Thanks for Playing!
            </motion.h1>
            <motion.p
              className="mt-24 text-2xl font-bold text-[#20145F] text-center max-w-md"
              initial={{ opacity: 0, y: -50 }}
              animate={{
                opacity: 1,
                y: 0,
                transition: {
                  delay: 0.2,
                  type: 'spring',
                  stiffness: 100
                }
              }}
            >
              We appreciate you!
            </motion.p>
          </motion.div>
        )}

        {animationStage === 1 && (
          <motion.div
            key="transition"
            initial={{ opacity: 0, y: -50 }}
            animate={{
              opacity: 1,
              y: 0,
              transition: {
                type: 'spring',
                stiffness: 100
              }
            }}
            className="text-4xl font-extrabold text-[#20145F] text-center"
          >
            Let&apos;s see what you&apos;ve created!
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

/**
 * Displays a canvas with drawing controls and navigation
 */
const CardsScreen = forwardRef<Stage, CardsScreenProps>(
  ({ drawings, selectedIndex, onIndexChange, onDownload }, ref) => {
    return (
      <AnimatePresence mode="wait">
        <div className="absolute inset-0 min-h-screen min-w-screen h-full w-full flex items-center justify-center">
          <div className="max-w-lg w-full mx-auto px-8">
            {drawings[selectedIndex] && (
              <motion.div
                key="spotlight"
                initial={{ opacity: 0, y: -200 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -200 }}
                transition={{ duration: 0.3, type: 'spring', stiffness: 100 }}
                className="flex flex-col items-center justify-center"
              >
                <h4 className="mb-48 text-2xl px-8 font-bold text-white text-center">
                  {drawings[selectedIndex].prompt ?? 'Loading...'}
                </h4>
                <div className="w-[90%] h-[90%]">
                  <Canvas drawing={drawings[selectedIndex].drawing} isViewerMode={true} />
                  <CanvasShare
                    ref={ref}
                    prompt={drawings[selectedIndex].prompt}
                    drawing={drawings[selectedIndex].drawing}
                  />
                </div>
                <div className="flex justify-center w-full mt-24 gap-16">
                  <button
                    onClick={() => onIndexChange(selectedIndex - 1)}
                    disabled={selectedIndex === 0}
                    className="btn btn-sm btn-secondary"
                  >
                    <FontAwesomeIcon icon={faArrowLeft} className="mt-1" />
                  </button>

                  <button onClick={onDownload} className="btn btn-sm btn-secondary">
                    Download
                    <FontAwesomeIcon icon={faDownload} className="ml-8" />
                  </button>

                  <button
                    onClick={() => onIndexChange(selectedIndex + 1)}
                    disabled={selectedIndex === drawings.length - 1}
                    className="btn btn-sm btn-secondary"
                  >
                    <FontAwesomeIcon icon={faArrowRight} className="mt-4" />
                  </button>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </AnimatePresence>
    );
  }
);

CardsScreen.displayName = 'CardsScreen';

/**
 * Main component for displaying and managing drawing cards
 * Handles fetching drawings and managing view states
 */
export default function PlayerDrawingCards() {
  const client = useAmplifyApiClient();
  const canvas = useRef<Stage>(null);
  const session = useSession();
  const player = usePlayer();
  const { id: sessionIdParam } = useParams();

  const [drawings, setDrawings] = useState<{ prompt: string; drawing: LineProps[] }[]>([]);
  const [showCards, setShowCards] = useState(false);
  const [selectedDrawingIndex, setSelectedDrawingIndex] = useState<number>(0);

  /**
   * Fetches player drawings from the API
   */
  useEffect(() => {
    if (!player || !session) return;

    const fetchDrawings = async () => {
      try {
        // Fetch games for this session to map prompts by gameId
        const gamesResult = (await client.graphql({
          query: queries.gameList,
          authMode: 'none',
          variables: { sessionId: sessionIdParam as string }
        })) as any;
        const games: Array<{ id: string; promptText: string }> = gamesResult.data?.gameList ?? [];
        const promptByGameId = new Map(games.map((g) => [g.id, g.promptText]));

        // Fetch player cards for this player (filter by this session)
        const cardsResult = (await client.graphql({
          query: queries.playerCardListByPlayer,
          authMode: 'none',
          variables: { playerId: player.id }
        })) as any;
        const allCards: Array<{ gameId: string; sessionId: string; drawing: string }> =
          cardsResult.data?.playerCardListByPlayer ?? [];
        const cards = allCards.filter((c) => c.sessionId === sessionIdParam);

        setDrawings(
          cards.map((item) => ({
            prompt: promptByGameId.get(item.gameId) ?? 'N/A',
            drawing: JSON.parse(item.drawing ?? '[]')
          }))
        );
      } catch (error) {
        console.error('Error fetching drawings:', error);
      }
    };

    fetchDrawings();
  }, [player?.id, client, sessionIdParam]);

  /**
   * Handles downloading the current drawing as PNG
   */
  const handleDownload = async () => {
    if (!canvas.current) return;

    await canvas.current.toBlob({
      callback: async (blob) => {
        if (!blob) return;

        const data = {
          files: [
            new File([blob], 'image.png', {
              type: blob.type
            })
          ]
        };

        // Check if on Anroid or iOS
        if (navigator.userAgent.match(/Android|iPhone|iPad|iPod/i)) {
          const shareData = {
            text: 'Check out my drawing!',
            files: [
              new File([blob], 'image.png', {
                type: blob.type
              })
            ]
          };

          await navigator.share(shareData);
          return;
        } else {
          const link = document.createElement('a');
          link.download = 'image.png';
          link.href = URL.createObjectURL(data.files[0]);
          link.click();
        }
      }
    });
  };

  return (
    <>
      {!showCards ? (
        <AnimatedFeelingAfter />
      ) : (
        <motion.div
          key={showCards ? 'cards' : 'thanks'}
          className="fixed inset-0 z-[-1]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
        >
          <AnimatedDrawingCards />
        </motion.div>
      )}

      <div className="container mx-auto px-4">
        <AnimatePresence mode="wait">
          {!showCards ? (
            <ThanksScreen key="thanks" onTransitionToCards={() => setShowCards(true)} />
          ) : (
            <CardsScreen
              key="cards"
              drawings={drawings}
              selectedIndex={selectedDrawingIndex}
              onIndexChange={setSelectedDrawingIndex}
              onDownload={handleDownload}
              ref={canvas}
            />
          )}
        </AnimatePresence>
      </div>
    </>
  );
}
